/**
 * Notifications store.
 *
 * Fed by the shared push demux (`useNotificationStream`) from live
 * `notification.event` + replayed `notification.events` frames — on web over the
 * cloud bridge, on desktop over the daemon's `/events` relay
 * (events ADR-012), and NEVER by REST polling. Mirrors the errors store shape so the combined nav badge
 * can source both. A notification `kind` (info/success) carries NO
 * severity — it contributes a COUNT only.
 */

import { atom } from 'jotai';
import type { CredentialUnreadableCause } from '@localagent/sdk';
import { configureAtomDevtools } from '../utils/tracking';
import { unreadErrorCountAtom } from '../errors/atoms';
import {
  notificationMutePrefsAtom,
  type NotificationEntryKind,
  type NotificationMutePrefs,
} from './mute-prefs';

export {
  notificationMutePrefsAtom,
  type NotificationEntryKind,
  type NotificationKind,
  type NotificationMutePrefs,
} from './mute-prefs';

export interface NotificationEntry {
  id: string;
  eventId: string;
  kind: NotificationEntryKind;
  title: string;
  body: string;
  serviceOrigin: string;
  sessionId?: string;
  workspaceId?: string;
  /** Optional structured payload surfaced by the details expander. */
  details?: Record<string, unknown>;
  timestamp: string;
  read: boolean;
}

export const UNREADABLE_CREDENTIAL_ORIGIN = 'shogunchop-desktop';

/** The user-visible half of a discarded-credential event: a discarded saved sign-in explains itself and
 *  directs the user to sign in again (sdk ADR-014). */
export function unreadableCredentialNotification(
  cause: CredentialUnreadableCause,
): NotificationEntry {
  return {
    id: `credential-unreadable-${cause}`,
    eventId: `credential-unreadable-${cause}`,
    kind: 'warning',
    title: "Your saved sign-in couldn't be read",
    body: 'It was discarded, so please sign in again.',
    serviceOrigin: UNREADABLE_CREDENTIAL_ORIGIN,
    details: { cause },
    timestamp: new Date().toISOString(),
    read: false,
  };
}

export const notificationEntriesAtom = configureAtomDevtools(
  atom<NotificationEntry[]>([]),
  'notifications/entries',
);

export function isNotificationMuted(
  entry: Pick<NotificationEntry, 'kind' | 'serviceOrigin'>,
  prefs: NotificationMutePrefs,
): boolean {
  return (
    prefs.mutedKinds.some((kind) => kind === entry.kind) ||
    prefs.mutedOrigins.includes(entry.serviceOrigin)
  );
}

export const unreadNotificationCountAtom = configureAtomDevtools(
  atom<number>((get) => {
    const prefs = get(notificationMutePrefsAtom);
    return get(notificationEntriesAtom).filter((e) => !e.read && !isNotificationMuted(e, prefs)).length;
  }),
  'notifications/unreadCount',
);

export const combinedUnreadCountAtom = configureAtomDevtools(
  atom<number>((get) => get(unreadErrorCountAtom) + get(unreadNotificationCountAtom)),
  'notifications/combinedUnreadCount',
);

export const addNotificationEntryAction = configureAtomDevtools(
  atom<null, [NotificationEntry], void>(null, (get, set, entry: NotificationEntry) => {
    const entries = get(notificationEntriesAtom);
    if (entries.some((e) => e.eventId === entry.eventId)) return;
    set(notificationEntriesAtom, [entry, ...entries].slice(0, 100));
  }),
  'notifications/addEntry',
);

export const markNotificationReadAction = configureAtomDevtools(
  atom<null, [string], void>(null, (get, set, eventId: string) => {
    set(
      notificationEntriesAtom,
      get(notificationEntriesAtom).map((e) => (e.eventId === eventId ? { ...e, read: true } : e)),
    );
  }),
  'notifications/markRead',
);

export const markAllNotificationsReadAction = configureAtomDevtools(
  atom<null, [], void>(null, (get, set) => {
    set(
      notificationEntriesAtom,
      get(notificationEntriesAtom).map((e) => ({ ...e, read: true })),
    );
  }),
  'notifications/markAllRead',
);

export const dismissNotificationAction = configureAtomDevtools(
  atom<null, [string], void>(null, (get, set, eventId: string) => {
    set(
      notificationEntriesAtom,
      get(notificationEntriesAtom).filter((e) => e.eventId !== eventId),
    );
  }),
  'notifications/dismiss',
);

export const setNotificationReadStateAction = configureAtomDevtools(
  atom<null, [{ eventId: string; read: boolean }], void>(
    null,
    (get, set, { eventId, read }) => {
      set(
        notificationEntriesAtom,
        get(notificationEntriesAtom).map((e) => (e.eventId === eventId ? { ...e, read } : e)),
      );
    },
  ),
  'notifications/setReadState',
);

export const clearNotificationsAction = configureAtomDevtools(
  atom<null, [], void>(null, (_get, set) => {
    set(notificationEntriesAtom, []);
  }),
  'notifications/clear',
);
